// screens/marketplace/MarketplaceProducts.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Package,
  Plus,
  X,
  Pencil,
  Star,
  StarOff,
  CheckCircle2,
  XCircle,
  Search,
  Power,
  Truck,
  Layers,
  Trash2,
  AlertCircle,
} from "lucide-react";
import { useAuth } from "../../providers/AuthProvider";
import { useToast } from "../../providers/ToastProvider";
import {
  PageShell,
  Section,
  Button,
  Field,
  Input,
  Textarea,
  Select,
  FormGrid,
  GridSpan,
  EmptyState,
  LoadingBlock,
  parseApiError,
  optimizeImage,
  useTokens,
} from "./shared";
import {
  buildVariantKey,
  generateCombinations,
  minBasePrices,
  validateVariants,
} from "../../utils/variants";

/* ══════════════════════════════════════════════════════════
   Helpers
   ══════════════════════════════════════════════════════════ */

const EMPTY_PRODUCT = {
  name: "",
  slug: "",
  short_description: "",
  description: "",
  category: "",
  sale_type: "DROPSHIPPING",
  base_price_customer: "",
  base_price_agent: "",
  pre_order_price_customer: "",
  pre_order_price_agent: "",
  stock_quantity: 0,
  shipping_fee: "0.00",
  main_image: "",
  is_in_stock: true,
  is_active: true,
  has_variants: false,
  // Wholesale
  is_wholesale: false,
  moq: "",
};

const SALE_TYPES = [
  { value: "DROPSHIPPING", label: "Ready to ship" },
  { value: "PRE_ORDER", label: "Pre-order only" },
  { value: "BOTH", label: "Both methods" },
  { value: "AVAILABLE_IN_GHANA", label: "Available in Ghana" },
];

const saleTypeLabel = (v) =>
  SALE_TYPES.find((s) => s.value === v)?.label || v || "—";

/* ── Attribute builder state helpers ─────────────────────── */

const newAttr = () => ({
  id: Math.random().toString(36).slice(2),
  name: "",
  values: [],
});

const newVariantRow = () => ({
  key: "",
  attributes: {},
  stock: "",
  base_price_customer: "",
  base_price_agent: "",
  pre_order_price_customer: "",
  pre_order_price_agent: "",
  sku: "",
});

/* ══════════════════════════════════════════════════════════
   Main screen
   ══════════════════════════════════════════════════════════ */

export default function MarketplaceProducts() {
  const { authFetch } = useAuth();
  const { showToast } = useToast();
  const t = useTokens();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [busyIds, setBusyIds] = useState(() => new Set());

  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [attributes, setAttributes] = useState([]); // [{ id, name, values[] }]
  const [variants, setVariants] = useState([]); // array of variant rows

  /* ── Load ─────────────────────────────────────────────── */
  const load = useCallback(async () => {
    try {
      const [pRes, cRes] = await Promise.all([
        authFetch("/marketplace/products/"),
        authFetch("/marketplace/categories/"),
      ]);
      const [pData, cData] = await Promise.all([pRes.json(), cRes.json()]);
      if (pRes.ok) setProducts(pData.data || []);
      if (cRes.ok) setCategories(cData.data || []);
    } catch {
      showToast("Could not load products", "error");
    } finally {
      setLoading(false);
    }
  }, [authFetch, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  /* ── Filtered list ────────────────────────────────────── */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter(
      (p) =>
        p.name?.toLowerCase().includes(q) ||
        p.category_name?.toLowerCase().includes(q)
    );
  }, [products, search]);

  /* ── Open modals ──────────────────────────────────────── */
  const openCreate = () => {
    setForm(EMPTY_PRODUCT);
    setAttributes([]);
    setVariants([]);
    setEditing(null);
    setShowForm(true);
  };

  const openEdit = (p) => {
    setForm({
      ...EMPTY_PRODUCT,
      ...p,
      category: p.category || "",
      main_image: "",
      has_variants: !!p.has_variants,
      shipping_fee: String(p.shipping_fee ?? "0.00"),
    });

    // Rebuild attribute rows from the product's variant_attributes
    // and the union of every variant's attributes.
    const declared = Array.isArray(p.variant_attributes)
      ? p.variant_attributes
      : [];
    const valuesByName = {};
    (p.variants || []).forEach((v) => {
      Object.entries(v.attributes || {}).forEach(([name, value]) => {
        if (!valuesByName[name]) valuesByName[name] = [];
        if (!valuesByName[name].includes(value)) valuesByName[name].push(value);
      });
    });
    const names = [
      ...declared.filter((n) => valuesByName[n]),
      ...Object.keys(valuesByName).filter((n) => !declared.includes(n)),
    ];
    setAttributes(
      names.map((name) => ({
        id: Math.random().toString(36).slice(2),
        name,
        values: valuesByName[name],
      }))
    );

    setVariants(
      (p.variants || []).map((v) => ({
        key: v.key || "",
        attributes: v.attributes || {},
        stock: v.stock != null ? String(v.stock) : "",
        base_price_customer: String(v.base_price_customer ?? ""),
        base_price_agent: String(v.base_price_agent ?? ""),
        pre_order_price_customer: String(v.pre_order_price_customer ?? ""),
        pre_order_price_agent: String(v.pre_order_price_agent ?? ""),
        sku: v.sku || "",
      }))
    );

    setEditing(p);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(EMPTY_PRODUCT);
    setAttributes([]);
    setVariants([]);
  };

  /* ── Attribute builder ────────────────────────────────── */
  const updateAttribute = (id, patch) =>
    setAttributes((prev) =>
      prev.map((a) => (a.id === id ? { ...a, ...patch } : a))
    );

  const addAttribute = () =>
    setAttributes((prev) => [...prev, newAttr()]);

  const removeAttribute = (id) =>
    setAttributes((prev) => prev.filter((a) => a.id !== id));

  const addValue = (id, value) => {
    const v = value.trim();
    if (!v) return;
    setAttributes((prev) =>
      prev.map((a) => {
        if (a.id !== id) return a;
        if (a.values.includes(v)) return a;
        return { ...a, values: [...a.values, v] };
      })
    );
  };

  const removeValue = (id, value) =>
    setAttributes((prev) =>
      prev.map((a) =>
        a.id === id ? { ...a, values: a.values.filter((x) => x !== value) } : a
      )
    );

  /* ── Regenerate variants when attribute grid changes ──── */
  // Whenever the attribute set changes, recompute the cartesian product
  // and merge with existing variant rows (preserving prices the user
  // already typed for keys that still exist).
  useEffect(() => {
    if (!form.has_variants) return;

    const validAttrs = attributes.filter(
      (a) => a.name.trim() && a.values.length > 0
    );
    if (validAttrs.length === 0) {
      setVariants([]);
      return;
    }

    const valuesByName = {};
    validAttrs.forEach((a) => {
      valuesByName[a.name.trim()] = a.values;
    });

    const combos = generateCombinations(valuesByName);
    const existingByKey = new Map(variants.map((v) => [v.key, v]));

    const next = combos.map((attrs) => {
      const key = buildVariantKey(attrs);
      const existing = existingByKey.get(key);
      return existing
        ? { ...existing, attributes: attrs } // keep user's typed values
        : { ...newVariantRow(), key, attributes: attrs };
    });

    setVariants(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributes, form.has_variants]);

  const updateVariant = (key, patch) =>
    setVariants((prev) =>
      prev.map((v) => (v.key === key ? { ...v, ...patch } : v))
    );

  /* ── Submit ───────────────────────────────────────────── */
  const submit = async (e) => {
    e.preventDefault();

    // Validate
    const errs = [];
    if (!form.name.trim()) errs.push("Name is required");
    if (!form.slug.trim()) errs.push("Slug is required");
    if (!form.category) errs.push("Category is required");

    if (form.has_variants) {
      if (variants.length === 0) {
        errs.push("Add at least one attribute with values to generate variants.");
      } else {
        const v = validateVariants(variants, form.sale_type);
        errs.push(...v.errors);
      }
    } else {
      if (!form.base_price_customer) errs.push("Customer price is required");
      if (!form.base_price_agent) errs.push("Agent price is required");
    }

    if (errs.length > 0) {
      errs.slice(0, 3).forEach((msg) => showToast(msg, "error"));
      return;
    }


    if (form.is_wholesale) {
      const moq = Number(form.moq);
      if (!Number.isInteger(moq) || moq < 2) {
        showToast("MOQ must be a whole number of at least 2", "error");
        return;
      }
    }

    setSubmitting(true);
    try {
      // Parent fallback prices: minimum of variants when has_variants.
      const prices = form.has_variants
        ? minBasePrices(variants)
        : {
          customer: form.base_price_customer,
          agent: form.base_price_agent,
        };

      const body = {
        name: form.name.trim(),
        slug: form.slug.trim(),
        short_description: form.short_description,
        description: form.description,
        category: form.category,
        sale_type: form.sale_type,
        has_variants: !!form.has_variants,
        is_wholesale: !!form.is_wholesale,
        ...(form.is_wholesale && { moq: Number(form.moq) }),
        variant_attributes: form.has_variants
          ? attributes
            .filter((a) => a.name.trim() && a.values.length > 0)
            .map((a) => a.name.trim())
          : [],
        base_price_customer: prices.customer,
        base_price_agent: prices.agent,
        stock_quantity: Number(form.stock_quantity) || 0,
        shipping_fee: form.shipping_fee || "0.00",
        is_in_stock: !!form.is_in_stock,
        is_active: !!form.is_active,
      };

      if (form.sale_type === "PRE_ORDER" || form.sale_type === "BOTH") {
        body.pre_order_price_customer = form.pre_order_price_customer;
        body.pre_order_price_agent = form.pre_order_price_agent;
      }

      if (form.has_variants) {
        body.variants = variants.map((v) => {
          const out = {
            key: v.key,
            attributes: v.attributes,
            base_price_customer: v.base_price_customer,
            base_price_agent: v.base_price_agent,
          };
          if (v.stock !== "") out.stock = Number(v.stock);
          if (v.sku) out.sku = v.sku;
          if (form.sale_type === "PRE_ORDER" || form.sale_type === "BOTH") {
            out.pre_order_price_customer = v.pre_order_price_customer;
            out.pre_order_price_agent = v.pre_order_price_agent;
          }
          return out;
        });
      }

      // File handling: FormData only if we have a File.
      const hasFile = form.main_image instanceof File;
      let payload;
      let headers = {};

      if (hasFile) {
        const fd = new FormData();
        for (const [k, v] of Object.entries(body)) {
          if (v === "" || v === null || v === undefined) continue;
          if (k === "variants") {
            fd.append(k, JSON.stringify(v));
          } else {
            fd.append(k, typeof v === "object" ? JSON.stringify(v) : v);
          }
        }
        fd.append("main_image", await optimizeImage(form.main_image));
        payload = fd;
      } else {
        payload = JSON.stringify(body);
        headers["Content-Type"] = "application/json";
      }

      const url = editing
        ? `/marketplace/products/${editing.id}/`
        : "/marketplace/products/";
      const res = await authFetch(url, {
        method: editing ? "PATCH" : "POST",
        body: payload,
        headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(parseApiError(data));

      showToast(editing ? "Product updated" : "Product published", "success");
      closeForm();
      load();
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Row actions ──────────────────────────────────────── */
  const patchProduct = async (p, patch, label) => {
    setBusyIds((s) => new Set(s).add(p.id));
    try {
      const res = await authFetch(`/marketplace/products/${p.id}/`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(parseApiError(d));
      }
      showToast(label, "success");
      setProducts((list) =>
        list.map((x) => (x.id === p.id ? { ...x, ...patch } : x))
      );
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setBusyIds((s) => {
        const n = new Set(s);
        n.delete(p.id);
        return n;
      });
    }
  };

  /* ══════════════════════════════════════════════════════
     Render
     ══════════════════════════════════════════════════════ */
  return (
    <PageShell
      eyebrow="Catalogue"
      title="Product catalogue"
      subtitle="Create products, set stock, and control visibility. Variants and shipping fees are managed here."
      actions={
        <>
          <Button variant="secondary" onClick={load} disabled={loading}>
            Refresh
          </Button>
          <Button icon={Plus} onClick={openCreate}>
            New product
          </Button>
        </>
      }
    >
      <Section
        title="Products"
        icon={Package}
        chip={`${filtered.length} of ${products.length}`}
      >
        <div style={{ marginBottom: 14, position: "relative", maxWidth: 380 }}>
          <Search
            size={15}
            color={t.muted}
            style={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
            }}
          />
          <Input
            placeholder="Search products…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ paddingLeft: 36 }}
          />
        </div>

        {loading ? (
          <LoadingBlock label="Loading products…" />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Package}
            title={search ? "No products match" : "No products yet"}
            description={
              search
                ? "Try a different search term."
                : "Publish your first product to start selling."
            }
            action={
              !search && (
                <Button icon={Plus} onClick={openCreate}>
                  New product
                </Button>
              )
            }
          />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                minWidth: 900,
                borderCollapse: "collapse",
              }}
            >
              <thead>
                <tr>
                  {[
                    "Product",
                    "Type",
                    "Variants",
                    "Customer",
                    "Agent",
                    "Shipping",
                    "Stock",
                    "Status",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: "left",
                        padding: "10px 12px",
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color: t.muted,
                        borderBottom: `1px solid ${t.border}`,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const busy = busyIds.has(p.id);
                  const variantCount = (p.variants || []).length;
                  const shipping = Number(p.shipping_fee || 0);
                  return (
                    <tr key={p.id} style={{ opacity: busy ? 0.6 : 1 }}>
                      <td style={cell(t)}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                          }}
                        >
                          <div
                            style={{
                              width: 38,
                              height: 38,
                              borderRadius: 8,
                              background: t.subtle,
                              overflow: "hidden",
                              flexShrink: 0,
                            }}
                          >
                            {p.main_image_url ? (
                              <img
                                src={p.main_image_url}
                                alt=""
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : null}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: 13.5,
                                fontWeight: 700,
                                color: t.heading,
                              }}
                            >
                              {p.name}
                            </div>
                            <div
                              style={{
                                fontSize: 11.5,
                                color: t.muted,
                                marginTop: 2,
                              }}
                            >
                              {p.category_name || "—"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td style={cell(t)}>
                        <Pill muted={t.muted} subtle={t.subtle}>
                          {saleTypeLabel(p.sale_type)}
                        </Pill>
                      </td>
                      <td style={cell(t)}>
                        {p.has_variants ? (
                          <Pill
                            fg={t.accent}
                            bg={`${t.accent}18`}
                          >
                            <Layers size={10} /> {variantCount}
                          </Pill>
                        ) : (
                          <span style={{ color: t.muted, fontSize: 12 }}>
                            —
                          </span>
                        )}
                      </td>
                      <td style={cell(t)}>
                        <strong style={{ color: t.heading }}>
                          GH₵{Number(p.base_price_customer || 0).toFixed(2)}
                        </strong>
                      </td>
                      <td style={cell(t)}>
                        <span style={{ color: t.accent, fontWeight: 700 }}>
                          GH₵{Number(p.base_price_agent || 0).toFixed(2)}
                        </span>
                      </td>
                      <td style={cell(t)}>
                        {shipping > 0 ? (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              color: t.text,
                              fontWeight: 600,
                              fontSize: 12.5,
                            }}
                          >
                            <Truck size={12} /> GH₵{shipping.toFixed(2)}
                          </span>
                        ) : (
                          <span
                            style={{
                              color: t.success,
                              fontWeight: 700,
                              fontSize: 12.5,
                            }}
                          >
                            Free
                          </span>
                        )}
                      </td>
                      <td style={cell(t)}>
                        {p.has_variants ? (
                          <span style={{ color: t.muted, fontSize: 12 }}>
                            per variant
                          </span>
                        ) : (
                          p.stock_quantity ?? 0
                        )}
                      </td>
                      <td style={cell(t)}>
                        <div
                          style={{
                            display: "flex",
                            gap: 6,
                            flexWrap: "wrap",
                          }}
                        >
                          <StatusPill
                            ok={!!p.is_active}
                            label={p.is_active ? "Active" : "Inactive"}
                            t={t}
                          />
                          {p.is_wholesale && Number(p.moq) >= 2 && (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                                padding: "3px 9px",
                                borderRadius: 999,
                                background: `${t.accent}18`,
                                color: t.accent,
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: 0.2,
                              }}
                            >
                              <Layers size={10} />
                              MOQ {Number(p.moq)}
                            </span>
                          )}
                          {!p.has_variants && (
                            <StatusPill
                              ok={!!p.is_in_stock}
                              label={p.is_in_stock ? "In stock" : "Out"}
                              t={t}
                            />
                          )}
                          {p.is_featured && (
                            <StatusPill ok label="Featured" t={t} tone="warn" />
                          )}
                        </div>
                      </td>
                      <td style={cell(t)}>
                        <div
                          style={{ display: "flex", gap: 6, flexWrap: "wrap" }}
                        >
                          <Button
                            size="sm"
                            variant="secondary"
                            icon={Pencil}
                            onClick={() => openEdit(p)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            icon={p.is_featured ? StarOff : Star}
                            onClick={() =>
                              patchProduct(
                                p,
                                { is_featured: !p.is_featured },
                                p.is_featured ? "Unfeatured" : "Featured"
                              )
                            }
                            disabled={busy}
                          />
                          <Button
                            size="sm"
                            variant="secondary"
                            icon={p.is_in_stock ? XCircle : CheckCircle2}
                            onClick={() =>
                              patchProduct(
                                p,
                                { is_in_stock: !p.is_in_stock },
                                "Stock updated"
                              )
                            }
                            disabled={busy}
                          />
                          <Button
                            size="sm"
                            variant={p.is_active ? "dangerGhost" : "secondary"}
                            icon={Power}
                            onClick={() =>
                              patchProduct(
                                p,
                                { is_active: !p.is_active },
                                p.is_active ? "Deactivated" : "Activated"
                              )
                            }
                            disabled={busy}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {showForm && (
        <ProductFormModal
          editing={editing}
          form={form}
          setForm={setForm}
          categories={categories}
          attributes={attributes}
          variants={variants}
          onAttributeChange={updateAttribute}
          onAttributeAdd={addAttribute}
          onAttributeRemove={removeAttribute}
          onValueAdd={addValue}
          onValueRemove={removeValue}
          onVariantChange={updateVariant}
          onClose={closeForm}
          onSubmit={submit}
          submitting={submitting}
          t={t}
        />
      )}
    </PageShell>
  );
}

/* ══════════════════════════════════════════════════════════
   Sub-components
   ══════════════════════════════════════════════════════════ */

const cell = (t) => ({
  padding: "12px",
  borderBottom: `1px solid ${t.separator}`,
  verticalAlign: "middle",
  fontSize: 13,
  color: t.text,
});

const Pill = ({ children, fg, bg, muted, subtle }) => {
  const finalFg = fg || muted;
  const finalBg = bg || subtle;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "3px 9px",
        borderRadius: 999,
        background: finalBg,
        color: finalFg,
        fontSize: 11.5,
        fontWeight: 700,
      }}
    >
      {children}
    </span>
  );
};

const StatusPill = ({ ok, label, t, tone }) => {
  const map =
    tone === "warn"
      ? { fg: t.warning, bg: t.warningBg }
      : ok
        ? { fg: t.success, bg: t.successBg }
        : { fg: t.muted, bg: t.subtle };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "3px 9px",
        borderRadius: 999,
        background: map.bg,
        color: map.fg,
        fontSize: 11,
        fontWeight: 700,
      }}
    >
      {label}
    </span>
  );
};

/* ══════════════════════════════════════════════════════════
   Product form modal
   ══════════════════════════════════════════════════════════ */

const ProductFormModal = ({
  editing,
  form,
  setForm,
  categories,
  attributes,
  variants,
  onAttributeChange,
  onAttributeAdd,
  onAttributeRemove,
  onValueAdd,
  onValueRemove,
  onVariantChange,
  onClose,
  onSubmit,
  submitting,
  t,
}) => {
  const needsPreorder =
    form.sale_type === "PRE_ORDER" || form.sale_type === "BOTH";

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed",
        inset: 0,
        background: t.scrim,
        backdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16,
      }}
    >
      <div
        style={{
          background: t.surfaceRaised,
          border: `1px solid ${t.border}`,
          borderRadius: 16,
          width: "100%",
          maxWidth: 860,
          maxHeight: "92vh",
          overflowY: "auto",
          padding: 24,
          boxShadow: `0 24px 64px ${t.shadowStrong}`,
          boxSizing: "border-box",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 800,
                color: t.heading,
                letterSpacing: "-0.01em",
              }}
            >
              {editing ? "Edit product" : "New product"}
            </div>
            <div
              style={{
                fontSize: 12.5,
                color: t.muted,
                marginTop: 2,
              }}
            >
              {editing
                ? "Update details, stock, variants, and pricing"
                : "Fill in the essentials — you can edit later"}
            </div>
          </div>
          <Button variant="ghost" size="sm" icon={X} onClick={onClose} />
        </div>

        <form onSubmit={onSubmit}>
          {/* ── Core fields ─────────────────────────────── */}
          <FormGrid columns={2}>
            <Field label="Product name">
              <Input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Field>
            <Field label="URL slug">
              <Input
                required
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
              />
            </Field>

            <Field label="Category">
              <Select
                required
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                <option value="">Choose category</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Sale type">
              <Select
                value={form.sale_type}
                onChange={(e) =>
                  setForm({ ...form, sale_type: e.target.value })
                }
              >
                {SALE_TYPES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </Select>
            </Field>

            {/* Wholesale toggle */}
            <GridSpan span="full">
              <WholesaleToggle
                form={form}
                setForm={setForm}
                colors={t}
              />
            </GridSpan>

            <Field
              label="Shipping fee (GH₵)"
              hint="Flat per-product. Leave empty or 0 for free shipping."
            >
              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.shipping_fee}
                onChange={(e) =>
                  setForm({ ...form, shipping_fee: e.target.value })
                }
                placeholder="0.00"
              />
            </Field>

            <Field
              label="Main image"
              hint={
                editing
                  ? "Leave empty to keep the current image"
                  : "JPG, PNG, or WebP"
              }
            >
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) =>
                  setForm({
                    ...form,
                    main_image: e.target.files?.[0] || "",
                  })
                }
                style={{
                  width: "100%",
                  padding: "8px 0",
                  fontSize: 13,
                  color: t.text,
                }}
              />
            </Field>

            <GridSpan span="full">
              <Field label="Short description" hint="Shown in listings">
                <Input
                  value={form.short_description}
                  onChange={(e) =>
                    setForm({ ...form, short_description: e.target.value })
                  }
                />
              </Field>
            </GridSpan>

            <GridSpan span="full">
              <Field label="Full description">
                <Textarea
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                />
              </Field>
            </GridSpan>
          </FormGrid>

          {/* ── Variant toggle ──────────────────────────── */}
          <div
            style={{
              marginTop: 22,
              paddingTop: 20,
              borderTop: `1px solid ${t.separator}`,
            }}
          >
            <VariantToggle
              form={form}
              setForm={setForm}
              t={t}
            />

            {form.has_variants && (
              <VariantBuilder
                attributes={attributes}
                variants={variants}
                onAttributeChange={onAttributeChange}
                onAttributeAdd={onAttributeAdd}
                onAttributeRemove={onAttributeRemove}
                onValueAdd={onValueAdd}
                onValueRemove={onValueRemove}
                onVariantChange={onVariantChange}
                needsPreorder={needsPreorder}
                t={t}
              />
            )}

            {!form.has_variants && (
              <SimplePricing
                form={form}
                setForm={setForm}
                needsPreorder={needsPreorder}
                t={t}
              />
            )}
          </div>

          {/* ── Actions ─────────────────────────────────── */}
          <div
            style={{
              display: "flex",
              gap: 10,
              marginTop: 24,
              justifyContent: "flex-end",
              paddingTop: 18,
              borderTop: `1px solid ${t.separator}`,
            }}
          >
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting
                ? "Saving…"
                : editing
                  ? "Save changes"
                  : "Publish product"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   Variant toggle
   ══════════════════════════════════════════════════════════ */

const VariantToggle = ({ form, setForm, t }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      padding: 14,
      borderRadius: 12,
      background: t.subtle,
      border: `1px solid ${t.border}`,
      marginBottom: 16,
    }}
  >
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 14,
          fontWeight: 700,
          color: t.heading,
        }}
      >
        <Layers size={15} color={t.accent} />
        Product has variants
      </div>
      <div
        style={{
          fontSize: 12,
          color: t.muted,
          marginTop: 3,
          lineHeight: 1.5,
        }}
      >
        Turn on for products with options like Size or Color. Customers will
        pick one combination before adding to cart.
      </div>
    </div>
    <Switch
      checked={!!form.has_variants}
      onChange={(v) => setForm({ ...form, has_variants: v })}
      t={t}
    />
  </div>
);

const Switch = ({ checked, onChange, t }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    style={{
      width: 46,
      height: 26,
      borderRadius: 13,
      border: "none",
      cursor: "pointer",
      background: checked ? t.accent : t.border,
      position: "relative",
      transition: "background 0.15s ease",
      flexShrink: 0,
    }}
  >
    <span
      style={{
        position: "absolute",
        top: 3,
        left: checked ? 23 : 3,
        width: 20,
        height: 20,
        borderRadius: "50%",
        background: "#fff",
        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
        transition: "left 0.15s ease",
      }}
    />
  </button>
);

/* ══════════════════════════════════════════════════════════
   Simple pricing (no variants)
   ══════════════════════════════════════════════════════════ */

const SimplePricing = ({ form, setForm, needsPreorder, t }) => (
  <FormGrid columns={2}>
    <Field label="Customer price (GH₵)">
      <Input
        required={!form.has_variants}
        type="number"
        min="0"
        step="0.01"
        value={form.base_price_customer}
        onChange={(e) =>
          setForm({ ...form, base_price_customer: e.target.value })
        }
      />
    </Field>
    <Field label="Agent cost (GH₵)">
      <Input
        required={!form.has_variants}
        type="number"
        min="0"
        step="0.01"
        value={form.base_price_agent}
        onChange={(e) =>
          setForm({ ...form, base_price_agent: e.target.value })
        }
      />
    </Field>

    {needsPreorder && (
      <>
        <Field label="Pre-order customer price (GH₵)">
          <Input
            required
            type="number"
            min="0"
            step="0.01"
            value={form.pre_order_price_customer}
            onChange={(e) =>
              setForm({
                ...form,
                pre_order_price_customer: e.target.value,
              })
            }
          />
        </Field>
        <Field label="Pre-order agent cost (GH₵)">
          <Input
            required
            type="number"
            min="0"
            step="0.01"
            value={form.pre_order_price_agent}
            onChange={(e) =>
              setForm({ ...form, pre_order_price_agent: e.target.value })
            }
          />
        </Field>
      </>
    )}

    <Field label="Stock quantity">
      <Input
        type="number"
        min="0"
        value={form.stock_quantity}
        onChange={(e) =>
          setForm({ ...form, stock_quantity: e.target.value })
        }
      />
    </Field>
  </FormGrid>
);


/* ══════════════════════════════════════════════════════════
   Wholesale toggle
   ══════════════════════════════════════════════════════════ */

const WholesaleToggle = ({ form, setForm, colors }) => {
  const on = !!form.is_wholesale;
  const moqNum = Number(form.moq);
  const moqValid = !on || (Number.isInteger(moqNum) && moqNum >= 2);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 14,
        padding: 14,
        borderRadius: 12,
        background: colors.subtle,
        border: `1px solid ${colors.border}`,
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 14,
            fontWeight: 700,
            color: colors.heading,
          }}
        >
          <Layers size={15} color={colors.accent} />
          Wholesale product
        </div>
        <div
          style={{
            fontSize: 12,
            color: colors.muted,
            marginTop: 3,
            lineHeight: 1.5,
          }}
        >
          Buyers must add at least the minimum order quantity. Prices,
          variants, and shipping work the same as any other product.
        </div>

        {on && (
          <div style={{ marginTop: 12, maxWidth: 200 }}>
            <Field
              label="Minimum order quantity"
              hint="Integer ≥ 2"
              error={
                !moqValid
                  ? "MOQ must be a whole number of at least 2."
                  : undefined
              }
            >
              <Input
                type="number"
                min="2"
                step="1"
                value={form.moq}
                onChange={(e) =>
                  setForm({ ...form, moq: e.target.value })
                }
                placeholder="e.g. 10"
              />
            </Field>
          </div>
        )}
      </div>

      <Switch
        checked={on}
        onChange={(v) =>
          setForm((prev) => ({
            ...prev,
            is_wholesale: v,
            moq: v ? prev.moq || "" : "",
          }))
        }
        t={colors}
      />
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   Variant builder
   ══════════════════════════════════════════════════════════ */

const VariantBuilder = ({
  attributes,
  variants,
  onAttributeChange,
  onAttributeAdd,
  onAttributeRemove,
  onValueAdd,
  onValueRemove,
  onVariantChange,
  needsPreorder,
  t,
}) => (
  <div style={{ display: "grid", gap: 20 }}>
    {/* Attribute builder */}
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: t.heading,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Layers size={14} color={t.accent} />
          Attributes
        </div>
        <Button
          size="sm"
          variant="secondary"
          icon={Plus}
          onClick={onAttributeAdd}
        >
          Add attribute
        </Button>
      </div>

      {attributes.length === 0 ? (
        <div
          style={{
            padding: 14,
            borderRadius: 10,
            background: t.warningBg,
            color: t.warning,
            fontSize: 12.5,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <AlertCircle size={15} />
          Add at least one attribute (e.g. "Size" with values S, M, L) to
          generate variants.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {attributes.map((a) => (
            <AttributeRow
              key={a.id}
              attr={a}
              onChange={(patch) => onAttributeChange(a.id, patch)}
              onRemove={() => onAttributeRemove(a.id)}
              onValueAdd={(v) => onValueAdd(a.id, v)}
              onValueRemove={(v) => onValueRemove(a.id, v)}
              t={t}
            />
          ))}
        </div>
      )}
    </div>

    {/* Variant grid */}
    {variants.length > 0 && (
      <div>
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: t.heading,
            marginBottom: 8,
          }}
        >
          Variants ({variants.length})
        </div>
        <div
          style={{
            border: `1px solid ${t.border}`,
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                minWidth: 720,
                borderCollapse: "collapse",
              }}
            >
              <thead>
                <tr style={{ background: t.subtle }}>
                  {[
                    "Combination",
                    "Stock",
                    "Customer (₵)",
                    "Agent (₵)",
                    ...(needsPreorder
                      ? ["Pre-order customer (₵)", "Pre-order agent (₵)"]
                      : []),
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: "left",
                        padding: "10px 12px",
                        fontSize: 10.5,
                        fontWeight: 700,
                        letterSpacing: "0.05em",
                        textTransform: "uppercase",
                        color: t.muted,
                        borderBottom: `1px solid ${t.border}`,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {variants.map((v) => (
                  <tr key={v.key}>
                    <td
                      style={{
                        padding: "10px 12px",
                        borderBottom: `1px solid ${t.separator}`,
                        fontSize: 12.5,
                        color: t.text,
                        fontWeight: 600,
                      }}
                    >
                      <code
                        style={{
                          fontFamily:
                            "ui-monospace, SFMono-Regular, Menlo, monospace",
                          fontSize: 11.5,
                          color: t.muted,
                        }}
                      >
                        {v.key}
                      </code>
                    </td>
                    <td style={gridCell(t)}>
                      <Input
                        type="number"
                        min="0"
                        value={v.stock}
                        onChange={(e) =>
                          onVariantChange(v.key, { stock: e.target.value })
                        }
                        placeholder="0"
                        style={{ padding: "6px 8px", fontSize: 13 }}
                      />
                    </td>
                    <td style={gridCell(t)}>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={v.base_price_customer}
                        onChange={(e) =>
                          onVariantChange(v.key, {
                            base_price_customer: e.target.value,
                          })
                        }
                        placeholder="0.00"
                        style={{ padding: "6px 8px", fontSize: 13 }}
                      />
                    </td>
                    <td style={gridCell(t)}>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={v.base_price_agent}
                        onChange={(e) =>
                          onVariantChange(v.key, {
                            base_price_agent: e.target.value,
                          })
                        }
                        placeholder="0.00"
                        style={{ padding: "6px 8px", fontSize: 13 }}
                      />
                    </td>
                    {needsPreorder && (
                      <>
                        <td style={gridCell(t)}>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={v.pre_order_price_customer}
                            onChange={(e) =>
                              onVariantChange(v.key, {
                                pre_order_price_customer: e.target.value,
                              })
                            }
                            placeholder="0.00"
                            style={{ padding: "6px 8px", fontSize: 13 }}
                          />
                        </td>
                        <td style={gridCell(t)}>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={v.pre_order_price_agent}
                            onChange={(e) =>
                              onVariantChange(v.key, {
                                pre_order_price_agent: e.target.value,
                              })
                            }
                            placeholder="0.00"
                            style={{ padding: "6px 8px", fontSize: 13 }}
                          />
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div
          style={{
            fontSize: 11.5,
            color: t.muted,
            marginTop: 8,
            lineHeight: 1.5,
          }}
        >
          The parent product's customer and agent prices default to the lowest
          variant prices. Stock on the parent is ignored when variants exist.
        </div>
      </div>
    )}
  </div>
);

const gridCell = (t) => ({
  padding: "8px 10px",
  borderBottom: `1px solid ${t.separator}`,
  verticalAlign: "middle",
  minWidth: 100,
});

/* ══════════════════════════════════════════════════════════
   Attribute row
   ══════════════════════════════════════════════════════════ */

const AttributeRow = ({
  attr,
  onChange,
  onRemove,
  onValueAdd,
  onValueRemove,
  t,
}) => {
  const [draft, setDraft] = useState("");

  const commit = () => {
    const v = draft.trim();
    if (!v) return;
    onValueAdd(v);
    setDraft("");
  };

  return (
    <div
      style={{
        padding: 12,
        borderRadius: 10,
        border: `1px solid ${t.border}`,
        background: t.surfaceRaised,
        display: "grid",
        gap: 10,
      }}
    >
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <Input
          value={attr.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="Attribute name (e.g. Size)"
          style={{ flex: 1 }}
        />
        <button
          type="button"
          onClick={onRemove}
          style={{
            width: 34,
            height: 34,
            borderRadius: 8,
            border: `1px solid ${t.border}`,
            background: "transparent",
            color: t.error,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
          aria-label="Remove attribute"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {/* Chips */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {attr.values.map((v) => (
          <span
            key={v}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 8px 4px 10px",
              borderRadius: 999,
              background: t.subtle,
              border: `1px solid ${t.border}`,
              fontSize: 12,
              fontWeight: 600,
              color: t.text,
            }}
          >
            {v}
            <button
              type="button"
              onClick={() => onValueRemove(v)}
              style={{
                background: "transparent",
                border: "none",
                color: t.muted,
                cursor: "pointer",
                padding: 0,
                display: "inline-flex",
                alignItems: "center",
              }}
              aria-label={`Remove ${v}`}
            >
              <X size={12} />
            </button>
          </span>
        ))}

        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "2px 4px",
            borderRadius: 999,
            background: t.surfaceRaised,
            border: `1px dashed ${t.border}`,
          }}
        >
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commit();
              }
            }}
            onBlur={commit}
            placeholder="Add value…"
            style={{
              width: 90,
              border: "none",
              background: "transparent",
              fontSize: 12,
              padding: "3px 6px",
              color: t.text,
              outline: "none",
              fontFamily: "inherit",
            }}
          />
        </div>
      </div>
    </div>
  );
};